import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Eye, RefreshCw, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import LoginPage from "./LoginPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const emptyProperty = {
  title: "",
  property_type: "appartement",
  transaction_type: "vente",
  location: "",
  price: "",
  surface: "",
  rooms: "",
  bathrooms: "",
  description: "",
  images: [""],
  featured: false,
  features: [""],
};

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyProperty);
  const [activeTab, setActiveTab] = useState("properties");
  const [syncing, setSyncing] = useState(false);
  const [apimoStatus, setApimoStatus] = useState(null);

  // Check existing token on mount
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => setAuthenticated(true))
        .catch(() => { localStorage.removeItem("admin_token"); })
        .finally(() => setCheckingAuth(false));
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const handleLogin = (token) => {
    setAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setAuthenticated(false);
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [propsRes, contactsRes, statsRes, apimoRes] = await Promise.all([
        axios.get(`${API}/properties`),
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/apimo/status`).catch(() => ({ data: null })),
      ]);
      setProperties(propsRes.data);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
      setApimoStatus(apimoRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
      console.error(err);
    }
  };

  useEffect(() => {
    if (authenticated) fetchData();
  }, [authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      price: parseInt(form.price) || 0,
      surface: form.surface ? parseInt(form.surface) : null,
      rooms: form.rooms ? parseFloat(form.rooms) : null,
      bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
      images: form.images.filter((img) => img.trim()),
      features: form.features.filter((f) => f.trim()),
    };

    try {
      const headers = getAuthHeader();
      if (editingId) {
        await axios.put(`${API}/properties/${editingId}`, data, { headers });
        toast.success("Bien mis à jour");
      } else {
        await axios.post(`${API}/properties`, data, { headers });
        toast.success("Bien ajouté");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyProperty);
      fetchData();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (property) => {
    setForm({
      ...property,
      price: property.price.toString(),
      surface: property.surface?.toString() || "",
      rooms: property.rooms?.toString() || "",
      bathrooms: property.bathrooms?.toString() || "",
      images: property.images?.length ? property.images : [""],
      features: property.features?.length ? property.features : [""],
    });
    setEditingId(property.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce bien ?")) return;
    try {
      await axios.delete(`${API}/properties/${id}`, { headers: getAuthHeader() });
      toast.success("Bien supprimé");
      fetchData();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleApimoSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/apimo/sync`, {}, { headers: getAuthHeader() });
      const { synced, removed, total_from_api } = res.data;
      toast.success(`Synchronisation APIMO réussie : ${synced} bien(s) synchronisé(s), ${removed} retiré(s) (${total_from_api} au total sur APIMO)`);
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la synchronisation APIMO");
    }
    setSyncing(false);
  };

  const addField = (field) => {
    setForm({ ...form, [field]: [...form[field], ""] });
  };

  const updateArrayField = (field, index, value) => {
    const updated = [...form[field]];
    updated[index] = value;
    setForm({ ...form, [field]: updated });
  };

  const removeArrayField = (field, index) => {
    const updated = form[field].filter((_, i) => i !== index);
    setForm({ ...form, [field]: updated.length ? updated : [""] });
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(price);

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-[#777] text-sm">Chargement...</div>
      </div>
    );
  }

  if (!authenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div data-testid="admin-page" className="pt-20 min-h-screen bg-[#FAFAFA]">
      <div className="px-6 lg:px-12 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="font-heading text-3xl font-medium text-[#333333] tracking-tight">
                Administration
              </h1>
              <p className="text-sm text-[#777] font-light mt-1">
                Gérez vos biens et demandes de contact
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="apimo-sync-btn"
                onClick={handleApimoSync}
                disabled={syncing}
                className="flex items-center gap-2 bg-[#333] text-white px-5 py-3 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#555] transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Sync..." : "Sync APIMO"}
              </button>
              <button
                data-testid="add-property-btn"
                onClick={() => {
                  setForm(emptyProperty);
                  setEditingId(null);
                  setShowForm(true);
                }}
                className="flex items-center gap-2 bg-[#699ab6] text-white px-6 py-3 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Ajouter un bien
              </button>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="flex items-center gap-2 border border-[#E5E5E5] text-[#777] px-4 py-3 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#F5F5F5] transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Biens", value: stats.properties || 0 },
              { label: "APIMO", value: stats.apimo_properties || 0 },
              { label: "Demandes", value: stats.contacts || 0 },
              { label: "Communes", value: stats.locations || 0 },
            ].map((s) => (
              <div key={s.label} className="bg-white p-6 border border-[#E5E5E5]">
                <p className="font-heading text-2xl font-semibold text-[#699ab6]">{s.value}</p>
                <p className="text-[11px] uppercase tracking-[0.1em] text-[#777] mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-6 mb-8 border-b border-[#E5E5E5]">
            {[
              { id: "properties", label: "Biens" },
              { id: "contacts", label: "Demandes" },
            ].map((tab) => (
              <button
                key={tab.id}
                data-testid={`admin-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-[11px] uppercase tracking-[0.15em] font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? "text-[#699ab6] border-[#699ab6]"
                    : "text-[#777] border-transparent hover:text-[#699ab6]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "properties" && (
            <div className="space-y-3">
              {properties.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-property-${p.id}`}
                  className="bg-white border border-[#E5E5E5] p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  <div className="w-16 h-16 flex-shrink-0 bg-[#F5F5F5] overflow-hidden">
                    <img src={p.images?.[0] || ""} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-heading text-base font-medium text-[#333] truncate">{p.title}</p>
                      {p.source === "apimo" && (
                        <span className="flex-shrink-0 text-[9px] uppercase tracking-wider bg-[#699ab6] text-white px-1.5 py-0.5 font-bold">APIMO</span>
                      )}
                    </div>
                    <p className="text-xs text-[#777]">
                      {p.reference && <span className="text-[#699ab6] font-medium">Réf. {p.reference} — </span>}
                      {p.location} — {formatPrice(p.price)} — {p.property_type}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/biens/${p.id}`} className="p-2 text-[#777] hover:text-[#699ab6] transition-colors">
                      <Eye className="w-4 h-4" />
                    </Link>
                    <button data-testid={`edit-property-${p.id}`} onClick={() => handleEdit(p)} className="p-2 text-[#777] hover:text-[#699ab6] transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-property-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 text-[#777] hover:text-red-600 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "contacts" && (
            <div className="space-y-3">
              {contacts.length === 0 ? (
                <p className="text-[#777] text-sm py-8 text-center">Aucune demande pour le moment</p>
              ) : (
                contacts.map((c) => (
                  <div key={c.id} data-testid={`contact-item-${c.id}`} className="bg-white border border-[#E5E5E5] p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-[#333] text-sm">{c.name}</p>
                        <p className="text-xs text-[#777]">{c.email} {c.phone && `— ${c.phone}`}</p>
                      </div>
                      <span className="text-[10px] text-[#999]">{new Date(c.created_at).toLocaleDateString("fr-CH")}</span>
                    </div>
                    {c.subject && <p className="text-xs text-[#699ab6] font-medium mb-1">{c.subject}</p>}
                    <p className="text-sm text-[#777] font-light">{c.message}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 overflow-y-auto">
          <div data-testid="property-form-modal" className="bg-white w-full max-w-2xl mx-4 my-8 p-8 relative">
            <button data-testid="close-form-btn" onClick={() => { setShowForm(false); setEditingId(null); }} className="absolute top-4 right-4 text-[#777] hover:text-[#333]">
              <X className="w-5 h-5" />
            </button>

            <h2 className="font-heading text-2xl font-medium text-[#333] mb-6">
              {editingId ? "Modifier le bien" : "Ajouter un bien"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Titre *</label>
                <input data-testid="form-title" type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-underline w-full text-sm" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Type *</label>
                  <select data-testid="form-type" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="input-underline w-full text-sm">
                    <option value="appartement">Appartement</option>
                    <option value="maison">Maison</option>
                    <option value="villa">Villa</option>
                    <option value="terrain">Terrain</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Transaction *</label>
                  <select data-testid="form-transaction" value={form.transaction_type} onChange={(e) => setForm({ ...form, transaction_type: e.target.value })} className="input-underline w-full text-sm">
                    <option value="vente">Vente</option>
                    <option value="location">Location</option>
                    <option value="programme">Programme neuf</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Localisation *</label>
                  <input data-testid="form-location" type="text" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-underline w-full text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Prix (CHF) *</label>
                  <input data-testid="form-price" type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-underline w-full text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Surface (m²)</label>
                  <input data-testid="form-surface" type="number" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} className="input-underline w-full text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Pièces</label>
                  <input data-testid="form-rooms" type="number" step="0.5" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} className="input-underline w-full text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">SdB</label>
                  <input data-testid="form-bathrooms" type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className="input-underline w-full text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">Description</label>
                <textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-underline w-full text-sm resize-none" />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">URLs des images</label>
                {form.images.map((img, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="url" value={img} onChange={(e) => updateArrayField("images", i, e.target.value)} className="input-underline flex-1 text-sm" placeholder="https://..." />
                    <button type="button" onClick={() => removeArrayField("images", i)} className="text-[#999] hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => addField("images")} className="text-xs text-[#699ab6] hover:text-[#333]">+ Ajouter une image</button>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">Caractéristiques</label>
                {form.features.map((f, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="text" value={f} onChange={(e) => updateArrayField("features", i, e.target.value)} className="input-underline flex-1 text-sm" placeholder="Ex: Jardin, Terrasse..." />
                    <button type="button" onClick={() => removeArrayField("features", i)} className="text-[#999] hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => addField("features")} className="text-xs text-[#699ab6] hover:text-[#333]">+ Ajouter une caractéristique</button>
              </div>

              <div className="flex items-center gap-2">
                <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4" />
                <label className="text-sm text-[#777]">Mettre en avant sur la page d'accueil</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button data-testid="form-submit-btn" type="submit" className="bg-[#699ab6] text-white px-8 py-3 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-colors">
                  {editingId ? "Mettre à jour" : "Ajouter"}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-8 py-3 text-[11px] uppercase tracking-[0.15em] font-medium text-[#777] border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
